import React, { useEffect, useState } from 'react';
import AuthPageContainer from './AuthPageContainer';
import { Link, useLocation } from 'react-router-dom';
import { Box, Button, Divider, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useMutation } from '@tanstack/react-query';
import { resendValidateEmail } from 'api/userApi';
import { useSnackbar } from 'notistack';

const EMAIL_RESEND_TIME_GAP = 5 * 60 * 1000; // 5 minutes

const VerifyEmailScreen = () => {
  const location = useLocation();
  const state = location.state as { email?: string } | null;
  const initialEmail = state?.email || '';
  const { enqueueSnackbar } = useSnackbar();

  const [isEmailVerificationButtonShown, setIsEmailVerificationButtonShown] =
    useState(false);

  const { mutate: handleResendClick, isLoading } = useMutation(
    ['resend-verify-email', initialEmail],
    resendValidateEmail,
    {
      onSuccess: () => {
        localStorage.setItem(
          `emailSentTime:${initialEmail}`,
          new Date().toISOString()
        );
        setIsEmailVerificationButtonShown(false);
        enqueueSnackbar('Verification email resent successfully!', {
          variant: 'success',
        });
      },
      onError: (err: any) => {
        enqueueSnackbar(
          err?.data?.message ||
            'Failed to resend verification email. Please try again later.',
          { variant: 'error' }
        );
      },
    }
  );
  const emailSentTimeLog = localStorage.getItem(
    `emailSentTime:${state?.email}`
  );

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (
      emailSentTimeLog &&
      Date.now() - new Date(emailSentTimeLog).getTime() < EMAIL_RESEND_TIME_GAP
    ) {
      setIsEmailVerificationButtonShown(false);
      const remaining =
        EMAIL_RESEND_TIME_GAP -
        (Date.now() - new Date(emailSentTimeLog).getTime());
      timer = setTimeout(() => {
        setIsEmailVerificationButtonShown(true);
      }, remaining);
    } else {
      setIsEmailVerificationButtonShown(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [state?.email, emailSentTimeLog]);

  return (
    <AuthPageContainer
      title="Welcome to Restorize!"
      subheader="Please verify your email to continue with Restorize."
      isLoading={false}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        gap={2}>
        <MailOutlineIcon sx={{ fontSize: 60, color: 'primary.main', mb: 1 }} />

        <Box
          sx={{
            backgroundColor: '#f5f7fa',
            borderRadius: 2,
            p: 2,
            mb: 2,
            boxShadow: 1,
            width: { xs: '100%' },
          }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            We just sent a verification email to
          </Typography>
          {initialEmail && (
            <Typography
              variant="h6"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                wordBreak: 'break-all',
                mb: 1,
              }}>
              {initialEmail}
            </Typography>
          )}{' '}
          <Typography variant="body1" sx={{ mb: 1 }}>
            Open it and follow the steps to complete your account activation.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you don’t see the email, check your spam or junk folder.
          </Typography>
        </Box>

        {isEmailVerificationButtonShown && (
          <>
            <Typography variant="body2" color="text.secondary">
              Didn’t receive the email?
            </Typography>

            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleResendClick(initialEmail)}
              disabled={isLoading}
              sx={{ textTransform: 'none' }}>
              {isLoading ? 'Resending...' : 'Resend Verification Email'}
            </Button>
          </>
        )}

        <Divider flexItem sx={{ my: 3 }} />

        <Typography variant="body2">
          Once verified, you can{' '}
          <Link to="/login" style={{ color: '#1976d2', fontWeight: 500 }}>
            log in
          </Link>{' '}
          and start using Restorize.
        </Typography>
      </Box>
    </AuthPageContainer>
  );
};

export default VerifyEmailScreen;
